import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Box, Flex, Text, Button, Tooltip,
} from "@chakra-ui/react";
import { ArrowUturnLeftIcon, CheckIcon } from "@heroicons/react/24/outline";
import AnswerCanvas from "./AnswerCanvas";
import { PALETTES } from "../appColors";

const PAD = 24;

/**
 * A figure, opened big enough to work on.
 *
 * This is the second way a diagram reaches an answer. The first is the add
 * menu, which brings a clean copy across; this one brings it across *worked
 * on* — a student opens the figure printed under 1 a), draws the construction
 * the question asks for, and attaches the result to 1 b) ii). The mismatch
 * between where a figure is printed and where its answer belongs is the whole
 * reason both exist.
 *
 * The pen is live the moment it opens, because nothing else here is worth a
 * click first.
 *
 * Rendered in a portal, so its colours come from the literal palette: the
 * CSS variables that carry the theme live on the app wrapper, which a portal
 * is outside of.
 */
const FigureStudio = ({ isOpen, onClose, figure, parts = [], onAttach, onDetach, isDark = false }) => {
  const p = isDark ? PALETTES.dark : PALETTES.light;
  const stageRef = useRef(null);
  const frameRef = useRef(null);
  const [strokes, setStrokes] = useState([]);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [attachedTo, setAttachedTo] = useState(() => new Set());

  // A fresh figure is a fresh drawing.
  useEffect(() => {
    if (isOpen) {
      setStrokes([]);
      setAttachedTo(new Set());
    }
  }, [isOpen, figure?.id]);

  /**
   * Fit the figure to whatever room the dialog has, without enlarging it.
   *
   * Measured through a callback ref rather than an effect: the dialog mounts
   * its body behind a transition, so an effect reading `.current` ran while
   * the frame was still null and left the canvas at zero.
   */
  const figureRef = useRef(figure);
  figureRef.current = figure;
  const observerRef = useRef(null);

  const measureFrame = useCallback((node) => {
    observerRef.current?.disconnect();
    frameRef.current = node;
    if (!node) return;

    const fit = (boxW, boxH) => {
      const f = figureRef.current;
      if (!f || boxW <= 0 || boxH <= 0) return;
      const scale = Math.min(
        (boxW - PAD * 2) / f.width,
        (boxH - PAD * 2) / f.height,
        1
      );
      setSize({
        width: Math.max(Math.round(f.width * scale), 1),
        height: Math.max(Math.round(f.height * scale), 1),
      });
    };

    const box = node.getBoundingClientRect();
    fit(box.width, box.height);

    const observer = new ResizeObserver(([entry]) =>
      fit(entry.contentRect.width, entry.contentRect.height)
    );
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const addStroke = useCallback((stroke) => setStrokes((prev) => [...prev, stroke]), []);
  const undo = useCallback(() => setStrokes((prev) => prev.slice(0, -1)), []);

  /**
   * Put this figure in a part's answer, or take it back out.
   *
   * A toggle, and a set rather than one value: attaching was a one-way door,
   * so changing your mind left the figure in the first part you clicked and
   * added it to the second as well. More than one part can legitimately want
   * the same diagram, which is why the set is not a single choice.
   */
  const toggleAttach = useCallback(
    (part) => {
      if (attachedTo.has(part.guid)) {
        onDetach?.(part.guid);
        setAttachedTo((prev) => {
          const next = new Set(prev);
          next.delete(part.guid);
          return next;
        });
        return;
      }
      /**
       * The figure and the marks on it, not a picture of the two together.
       *
       * Flattening here left a student with pixels: reopening the answer
       * showed a diagram they could no longer change, and nothing to undo.
       * The strokes travel as strokes and are only flattened when the answer
       * is submitted, where a flat picture is what the marker wants.
       *
       * Recorded against the figure rather than the canvas — a fraction of
       * its width and height — so they land correctly whatever size it is
       * drawn at next.
       */
      onAttach?.(part.guid, {
        image: figure.image,
        label: figure.label,
        strokes: strokes.map((stroke) => ({
          ...stroke,
          points: stroke.points.map((value, i) =>
            i % 2 === 0 ? value / size.width : value / size.height
          ),
        })),
      });
      setAttachedTo((prev) => new Set(prev).add(part.guid));
    },
    [attachedTo, onAttach, onDetach, figure, strokes, size]
  );

  if (!figure) return null;

  // The figure is the canvas's one attachment, pinned and centred.
  const asAttachment = [{
    id: figure.id,
    image: figure.image,
    width: size.width,
    height: size.height,
    x: 0,
    y: 0,
  }];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="rgba(0,0,0,0.6)" />
      <ModalContent bg={p.surface} color={p.text} borderRadius="16px" maxW="min(1100px, 94vw)">
        <ModalHeader fontSize="15px" fontWeight="500">
          {figure.label || "Figure"}
          <Text as="span" fontSize="11px" color={p.textSub} ml="10px">
            Draw on it, then attach it to a part of your answer
          </Text>
        </ModalHeader>
        <ModalCloseButton color={p.text} />

        <ModalBody pb="8px">
          <Flex
            ref={measureFrame}
            align="center"
            justify="center"
            height="min(60vh, 560px)"
            borderRadius="12px"
            bg={p.surfaceSubtle}
            border="1px solid"
            borderColor={p.border}
            overflow="hidden"
          >
            {size.width > 0 && (
              <Box width={`${size.width}px`} height={`${size.height}px`}>
                <AnswerCanvas
                  ref={stageRef}
                  width={size.width}
                  height={size.height}
                  attachments={asAttachment}
                  strokes={strokes}
                  onAddStroke={addStroke}
                  onMoveAttachment={() => {}}
                  ground="plain"
                  isDark={isDark}
                />
              </Box>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter gap="10px" flexWrap="wrap" justifyContent="space-between">
          <Flex align="center" gap="8px" flexWrap="wrap" minW={0}>
            <Text fontSize="11px" color={p.textSub} mr="2px">
              Attach to
            </Text>
            {parts.length === 0 && (
              <Text fontSize="11px" color={p.textMuted}>
                This question has no parts that take an answer.
              </Text>
            )}
            {parts.map((part) => {
              const done = attachedTo.has(part.guid);
              return (
                <Tooltip
                  key={part.guid}
                  label={done ? `Remove from ${part.short}` : `Put this in your ${part.short} answer`}
                  placement="top"
                  hasArrow
                  fontSize="11px"
                >
                  <Button
                    size="xs"
                    height="28px"
                    px="14px"
                    borderRadius="200px"
                    variant="outline"
                    borderColor={done ? p.accent : p.border}
                    color={done ? p.accent : p.text}
                    bg="transparent"
                    fontWeight="400"
                    _hover={{ bg: p.hover }}
                    leftIcon={done ? <CheckIcon style={{ width: 12, height: 12 }} /> : undefined}
                    onClick={() => toggleAttach(part)}
                  >
                    {part.short}
                  </Button>
                </Tooltip>
              );
            })}
          </Flex>

          <Flex align="center" gap="8px">
            <Button
              size="sm"
              variant="ghost"
              borderRadius="200px"
              px="16px"
              color={p.text}
              isDisabled={!strokes.length}
              leftIcon={<ArrowUturnLeftIcon style={{ width: 14, height: 14 }} />}
              onClick={undo}
            >
              Undo
            </Button>
            {/* On a dark ground the brand orange is the loudest thing in the
                dialog and pulls the eye off the drawing, so the confirm is
                simply the ink inverted. */}
            <Button
              size="sm"
              borderRadius="200px"
              px="20px"
              bg={isDark ? p.text : p.accent}
              color={isDark ? p.surface : p.onAccent}
              _hover={{ bg: isDark ? p.text2 : p.accentHover }}
              onClick={onClose}
            >
              Done
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default memo(FigureStudio);
